import { logger } from './logger.js';
export var FeeRuleType;
(function (FeeRuleType) {
    FeeRuleType["PRICE"] = "price";
    FeeRuleType["QUANTITY"] = "quantity";
    FeeRuleType["SOURCE"] = "source";
    FeeRuleType["PAYMENT_METHOD"] = "payment_method";
    FeeRuleType["PRODUCT_TYPE"] = "product_type";
    FeeRuleType["FLAT"] = "flat";
})(FeeRuleType || (FeeRuleType = {}));
export var FeeCategory;
(function (FeeCategory) {
    FeeCategory["SERVICE"] = "service";
    FeeCategory["PROCESSING"] = "processing";
    FeeCategory["TAX"] = "tax";
    FeeCategory["CHARGEBACK_PROTECTION"] = "chargeback_protection";
    FeeCategory["FACILITY"] = "facility";
    FeeCategory["RESALE_ORGANIZER"] = "resale_organizer";
    FeeCategory["RESALE_PLATFORM"] = "resale_platform";
})(FeeCategory || (FeeCategory = {}));
export var FeeCalculationMethod;
(function (FeeCalculationMethod) {
    FeeCalculationMethod["FIXED"] = "fixed";
    FeeCalculationMethod["PERCENTAGE"] = "percentage";
})(FeeCalculationMethod || (FeeCalculationMethod = {}));
/**
 * Rounds a number to 2 decimal places to handle currency accurately.
 */
const roundToCents = (amount) => {
    return Math.round(amount * 100) / 100;
};
/** Treat null/undefined/NaN as "no cap" for tiered rules (JSON configs often use null). */
const finiteCap = (v) => {
    if (v === null || v === undefined)
        return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
};
export function resaleSplitCategory(rule) {
    const category = String(rule?.category || '')
        .trim()
        .toLowerCase();
    const name = String(rule?.name || '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');
    if (category === FeeCategory.RESALE_ORGANIZER ||
        name === FeeCategory.RESALE_ORGANIZER) {
        return FeeCategory.RESALE_ORGANIZER;
    }
    if (category === FeeCategory.RESALE_PLATFORM ||
        name === FeeCategory.RESALE_PLATFORM) {
        return FeeCategory.RESALE_PLATFORM;
    }
    return null;
}
/**
 * Seller-side resale fee. Each matching rule is a share of the asking price.
 * Organizer and platform shares are calculated separately, then added.
 * A 4% organizer rule and a 6% platform rule on an $80 ticket is an $8 fee:
 * $3.20 to the organizer and $4.80 to the platform.
 */
export function calculateResaleSellerFee(askingPrice, rules = []) {
    const price = Number(askingPrice);
    const safePrice = Number.isFinite(price) && price > 0 ? price : 0;
    const list = Array.isArray(rules) ? rules : [];
    const shareFor = (category) => {
        const categoryRules = list.filter((rule) => resaleSplitCategory(rule) === category);
        if (!categoryRules.length || safePrice <= 0)
            return 0;
        let total = 0;
        for (const rule of categoryRules) {
            const maxPrice = finiteCap(rule.maxPrice);
            if (maxPrice !== undefined && safePrice > maxPrice)
                continue;
            const maxQuantity = finiteCap(rule.maxQuantity);
            if (rule.type === FeeRuleType.QUANTITY &&
                maxQuantity !== undefined &&
                maxQuantity < 1) {
                continue;
            }
            const raw = Number(rule.value);
            const amount = Number.isFinite(raw) ? raw : 0;
            total = roundToCents(total +
                (rule.method === FeeCalculationMethod.PERCENTAGE
                    ? roundToCents((safePrice * amount) / 100)
                    : amount));
            if (rule.exclusive !== false)
                break;
        }
        return total;
    };
    const organizerShare = shareFor(FeeCategory.RESALE_ORGANIZER);
    const platformShare = shareFor(FeeCategory.RESALE_PLATFORM);
    return {
        hasRules: list.some((rule) => resaleSplitCategory(rule) !== null),
        organizerShare,
        platformShare,
        sellerServiceFee: roundToCents(organizerShare + platformShare),
    };
}
/**
 * Calculates the per-item fees based on the item price, quantity, source, and provided rules.
 *
 * @param params - Object containing price, quantity, source, paymentMethod, and productType.
 * @param rules - An array of fee rules.
 * @returns An array of applied fees for the item.
 */
export const calculateItemFees = (params, rules) => {
    const { price, quantity, source, paymentMethod, productType } = params;
    const appliedFees = [];
    logger.debug('Calculating item fees', { params, ruleCount: rules.length });
    for (const rule of rules) {
        if (resaleSplitCategory(rule))
            continue;
        let matched = false;
        // Check constraints based on rule type and any additional constraints provided
        switch (rule.type) {
            case FeeRuleType.PRICE: {
                if (price === undefined)
                    break;
                const cap = finiteCap(rule.maxPrice);
                if (cap === undefined || price <= cap) {
                    matched = true;
                }
                break;
            }
            case FeeRuleType.QUANTITY: {
                if (quantity === undefined)
                    break;
                const cap = finiteCap(rule.maxQuantity);
                if (cap === undefined || quantity <= cap) {
                    matched = true;
                }
                break;
            }
            case FeeRuleType.SOURCE:
                if (source !== undefined && rule.source === source) {
                    matched = true;
                }
                break;
            case FeeRuleType.PAYMENT_METHOD:
                if (paymentMethod !== undefined &&
                    rule.paymentMethod === paymentMethod) {
                    matched = true;
                }
                break;
            case FeeRuleType.PRODUCT_TYPE:
                if (productType !== undefined && rule.productType === productType) {
                    matched = true;
                }
                break;
            case FeeRuleType.FLAT:
                matched = true;
                break;
        }
        // If it matched the primary type, but has other constraints, verify those too
        if (matched) {
            const maxPriceCap = finiteCap(rule.maxPrice);
            if (maxPriceCap !== undefined &&
                (price === undefined || price > maxPriceCap)) {
                matched = false;
            }
            const maxQtyCap = finiteCap(rule.maxQuantity);
            if (maxQtyCap !== undefined &&
                (quantity === undefined || quantity > maxQtyCap)) {
                matched = false;
            }
            if (rule.source !== undefined && source !== rule.source) {
                matched = false;
            }
            if (rule.paymentMethod !== undefined &&
                paymentMethod !== rule.paymentMethod) {
                matched = false;
            }
            if (rule.productType !== undefined && productType !== rule.productType) {
                matched = false;
            }
        }
        if (matched) {
            let feeValue = rule.value;
            if (rule.method === FeeCalculationMethod.PERCENTAGE &&
                price !== undefined) {
                feeValue = roundToCents((price * rule.value) / 100);
            }
            appliedFees.push({
                name: rule.name || rule.category.toUpperCase(),
                category: rule.category,
                value: feeValue,
            });
            logger.debug('Fee rule matched', {
                ruleName: rule.name,
                category: rule.category,
                feeValue,
            });
            if (rule.exclusive !== false) {
                break;
            }
        }
    }
    return appliedFees;
};
/**
 * Legacy wrapper for backward compatibility.
 * @deprecated Use calculateItemFees instead.
 */
export const calculateItemFee = (params, rules) => {
    const fees = calculateItemFees(params, rules);
    return fees.reduce((sum, fee) => sum + fee.value, 0);
};
/**
 * Calculates detailed fees including service fees, processing fees, taxes and chargeback protection.
 *
 * @param params - Object containing price, quantity, source, paymentMethod, and productType.
 * @param config - Organization fee configuration.
 * @returns A breakdown of the calculated fees.
 */
export const calculateDetailedFees = (params, config) => {
    const { price, quantity, source, paymentMethod, productType } = params;
    const { rules } = config;
    const appliedFeesPerItem = calculateItemFees({ price, quantity, source, paymentMethod, productType }, rules);
    const breakdown = {
        serviceFee: 0,
        processingFee: 0,
        tax: 0,
        chargebackProtectionFee: 0,
        facilityFee: 0,
        totalFee: 0,
        totalPrice: 0,
        appliedRules: [],
    };
    const itemTotal = roundToCents(price * quantity);
    appliedFeesPerItem.forEach((fee) => {
        const totalFeeValue = roundToCents(fee.value * quantity);
        switch (fee.category) {
            case FeeCategory.SERVICE:
                breakdown.serviceFee = roundToCents(breakdown.serviceFee + totalFeeValue);
                break;
            case FeeCategory.PROCESSING:
                breakdown.processingFee = roundToCents(breakdown.processingFee + totalFeeValue);
                break;
            case FeeCategory.TAX:
                breakdown.tax = roundToCents(breakdown.tax + totalFeeValue);
                break;
            case FeeCategory.CHARGEBACK_PROTECTION:
                breakdown.chargebackProtectionFee = roundToCents(breakdown.chargebackProtectionFee + totalFeeValue);
                break;
            case FeeCategory.FACILITY:
                breakdown.facilityFee = roundToCents(breakdown.facilityFee + totalFeeValue);
                break;
        }
        breakdown.appliedRules.push({
            ...fee,
            value: totalFeeValue,
        });
    });
    breakdown.totalFee = roundToCents(breakdown.serviceFee +
        breakdown.processingFee +
        breakdown.tax +
        breakdown.chargebackProtectionFee +
        breakdown.facilityFee);
    breakdown.totalPrice = roundToCents(itemTotal + breakdown.totalFee);
    logger.info('Fee calculation complete', {
        organizationId: config.organizationId,
        price,
        quantity,
        totalFee: breakdown.totalFee,
        totalPrice: breakdown.totalPrice,
        appliedRuleCount: breakdown.appliedRules.length,
    });
    return breakdown;
};
/**
 * Validates the fee configuration for an organization.
 * Throws an error if the configuration is invalid.
 */
export const validateFeeConfig = (config) => {
    if (!config.organizationId) {
        throw new Error('organizationId is required');
    }
    if (!Array.isArray(config.rules)) {
        throw new Error('rules must be an array');
    }
    for (const rule of config.rules) {
        if (!rule.category) {
            throw new Error(`Category is required for rule: ${rule.name || rule.type}`);
        }
        if (rule.value < 0) {
            throw new Error(`Fee rule value cannot be negative: ${rule.name || rule.type}`);
        }
        if (rule.type === FeeRuleType.PRICE) {
            const mp = finiteCap(rule.maxPrice);
            if (mp !== undefined && mp < 0) {
                throw new Error('maxPrice cannot be negative');
            }
        }
        if (rule.type === FeeRuleType.QUANTITY) {
            const mq = finiteCap(rule.maxQuantity);
            if (mq !== undefined && mq < 0) {
                throw new Error('maxQuantity cannot be negative');
            }
        }
        if (rule.type === FeeRuleType.PAYMENT_METHOD && !rule.paymentMethod) {
            throw new Error(`paymentMethod is required for PAYMENT_METHOD rule type: ${rule.name || rule.category}`);
        }
        if (rule.type === FeeRuleType.PRODUCT_TYPE && !rule.productType) {
            throw new Error(`productType is required for PRODUCT_TYPE rule type: ${rule.name || rule.category}`);
        }
    }
};
